/**
 * Pure math/geometry/date utility functions extracted from NwmVariableService.
 * These have no dependencies on MapLibre, GeoTIFF, or any service state,
 * making them trivially testable.
 */

// ---- Date / Timestamp utilities ----

/**
 * Format a Date as a YYYYMMDDHH string.
 */
export function timestampFromDate(time: Date): string {
  const y = time.getUTCFullYear()
  const m = String(time.getUTCMonth() + 1).padStart(2, '0')
  const d = String(time.getUTCDate()).padStart(2, '0')
  const hh = String(time.getUTCHours()).padStart(2, '0')
  return `${y}${m}${d}${hh}`
}

/**
 * Snap a Date to the nearest available timestamp from a sorted list.
 * Returns the exact match, or the most recent timestamp <= the date,
 * or the last available timestamp as a fallback.
 */
export function snapToAvailableTimestamp(time: Date, availableTimestamps: string[]): string {
  if (availableTimestamps.length === 0) {
    return ''
  }

  // Detect format from the available timestamps (12-char = GOES, 10-char = NWM/HRRR)
  const isGoesFormat = availableTimestamps[0].length === 12
  const exact = isGoesFormat ? goesTimestampFromDate(time) : timestampFromDate(time)

  if (availableTimestamps.includes(exact)) {
    return exact
  }

  // Find most recent available timestamp <= current time
  for (let i = availableTimestamps.length - 1; i >= 0; i--) {
    if (availableTimestamps[i] <= exact) {
      return availableTimestamps[i]
    }
  }

  return availableTimestamps[availableTimestamps.length - 1]
}

/**
 * Parse a YYYYMMDDHH string into a Date.
 */
export function parseTimestamp(ts: string): Date {
  return new Date(
    Date.UTC(
      parseInt(ts.slice(0, 4)),
      parseInt(ts.slice(4, 6)) - 1,
      parseInt(ts.slice(6, 8)),
      parseInt(ts.slice(8, 10)),
      0,
      0
    )
  )
}

/**
 * Generate candidate YYYYMMDDHH timestamps around the current time.
 * Covers 6 hours in the past and 18 hours into the future.
 */
export function generateTimestampCandidates(now: Date): string[] {
  const timestamps: string[] = []
  for (let hourOffset = -6; hourOffset <= 18; hourOffset++) {
    const d = new Date(now.getTime() + hourOffset * 3600000)
    timestamps.push(timestampFromDate(d))
  }
  return timestamps
}

// ---- GOES timestamp utilities (15-minute granularity) ----

/**
 * Format a Date as a YYYYMMDDHHMM string, snapped to 15-minute boundary.
 */
export function goesTimestampFromDate(time: Date): string {
  const y = time.getUTCFullYear()
  const m = String(time.getUTCMonth() + 1).padStart(2, '0')
  const d = String(time.getUTCDate()).padStart(2, '0')
  const hh = String(time.getUTCHours()).padStart(2, '0')
  const mm = String(Math.floor(time.getUTCMinutes() / 15) * 15).padStart(2, '0')
  return `${y}${m}${d}${hh}${mm}`
}

/**
 * Parse a YYYYMMDDHHMM string into a Date.
 */
export function parseGoesTimestamp(ts: string): Date {
  return new Date(
    Date.UTC(
      parseInt(ts.slice(0, 4)),
      parseInt(ts.slice(4, 6)) - 1,
      parseInt(ts.slice(6, 8)),
      parseInt(ts.slice(8, 10)),
      parseInt(ts.slice(10, 12)),
      0
    )
  )
}

/**
 * Generate candidate YYYYMMDDHHMM timestamps for GOES data.
 * Covers 12 hours in the past at 15-minute intervals (observation-only, no future).
 */
export function generateGoesTimestampCandidates(now: Date): string[] {
  const timestamps: string[] = []
  for (let offset = -48; offset <= 0; offset++) {
    const d = new Date(now.getTime() + offset * 15 * 60000)
    timestamps.push(goesTimestampFromDate(d))
  }
  return timestamps
}

// ---- Tile math utilities ----

export interface TileBounds {
  west: number
  south: number
  east: number
  north: number
}

/**
 * Convert Web Mercator tile coordinates (z/x/y) to lat/lng bounding box.
 */
export function tileBoundsToLatLng(z: number, x: number, y: number): TileBounds {
  const n = Math.pow(2, z)
  const west = (x / n) * 360 - 180
  const east = ((x + 1) / n) * 360 - 180
  const north = (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI
  const south = (Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * 180) / Math.PI
  return { west, south, east, north }
}

/**
 * Map a zoom level to a GeoTIFF overview index.
 * Lower zoom levels use coarser overviews for performance.
 */
export function zoomToOverviewIndex(z: number, maxOverview = 3): number {
  if (z >= 10) return 0
  if (z >= 8) return Math.min(1, maxOverview)
  if (z >= 6) return Math.min(2, maxOverview)
  return Math.min(3, maxOverview)
}

// ---- Wind / vector geometry utilities ----

/**
 * Compute interpolation fraction (0-1) between two timestamps given the
 * current time. Returns 0 if interpolation isn't possible.
 */
export function computeWindFraction(
  currentTime: Date,
  currentTs: string,
  availableTimestamps: string[],
  hasNextData: boolean
): number {
  if (!hasNextData) {
    return 0
  }

  const tsIndex = availableTimestamps.indexOf(currentTs)
  if (tsIndex < 0 || tsIndex >= availableTimestamps.length - 1) {
    return 0
  }

  const curDate = parseTimestamp(currentTs)
  const nextDate = parseTimestamp(availableTimestamps[tsIndex + 1])
  const span = nextDate.getTime() - curDate.getTime()
  if (span <= 0) {
    return 0
  }

  return Math.max(0, Math.min(1, (currentTime.getTime() - curDate.getTime()) / span))
}

export interface WindGridParams {
  /** Degree spacing between arrow grid points */
  spacing: number
  /** Viewport bounds (clamped to data extent) */
  west: number
  east: number
  south: number
  north: number
  /** Bounding box of the full raster [minX, minY, maxX, maxY] */
  bbox: number[]
  /** Pixels per degree in the full-resolution image */
  pxPerDegX: number
  pxPerDegY: number
  /** Scale factors from full-res to overview */
  scaleX: number
  scaleY: number
  /** Pixel offset of the read window within the overview image */
  winLeft: number
  winTop: number
  /** Dimensions of the read window */
  winW: number
  winH: number
}

/**
 * Generate GeoJSON arrow features from wind raster data.
 *
 * @param params  Grid and pixel-mapping parameters
 * @param currentU  U-component raster (east-west wind, m/s)
 * @param currentV  V-component raster (north-south wind, m/s)
 * @param fraction  Interpolation fraction (0 = current hour only)
 * @param nextU  Next-hour U-component (required when fraction > 0)
 * @param nextV  Next-hour V-component (required when fraction > 0)
 * @returns GeoJSON FeatureCollection with MultiLineString arrow features
 */
export function generateWindGeoJSON(
  params: WindGridParams,
  currentU: Float32Array,
  currentV: Float32Array,
  fraction: number,
  nextU: Float32Array | null = null,
  nextV: Float32Array | null = null
): GeoJSON.FeatureCollection {
  const {
    spacing,
    west,
    east,
    south,
    north,
    bbox,
    pxPerDegX,
    pxPerDegY,
    scaleX,
    scaleY,
    winLeft,
    winTop,
    winW,
    winH,
  } = params

  const features: GeoJSON.Feature[] = []
  const arrowLen = spacing * 0.4
  const headAngle = 0.5 // ~30 degrees
  const nodata = -9999
  const hasNext = fraction > 0 && nextU && nextV

  for (let lat = south; lat <= north; lat += spacing) {
    for (let lng = west; lng <= east; lng += spacing) {
      const px = Math.round((lng - bbox[0]) * pxPerDegX * scaleX) - winLeft
      const py = Math.round((bbox[3] - lat) * pxPerDegY * scaleY) - winTop

      if (px < 0 || px >= winW || py < 0 || py >= winH) {
        continue
      }

      const idx = py * winW + px

      let u = currentU[idx]
      let v = currentV[idx]

      if (u <= nodata || v <= nodata || isNaN(u) || isNaN(v)) {
        continue
      }

      // Interpolate with next hour if available
      if (hasNext) {
        const u2 = nextU[idx]
        const v2 = nextV[idx]
        if (u2 > nodata && v2 > nodata && !isNaN(u2) && !isNaN(v2)) {
          u = u + (u2 - u) * fraction
          v = v + (v2 - v) * fraction
        }
      }

      const speed = Math.sqrt(u * u + v * v)
      const speedMph = speed * 2.23694
      if (speedMph < 0.5) {
        continue
      }

      const ux = u / speed
      const vy = v / speed

      const lenFactor = Math.min(1, 0.3 + (speedMph / 40) * 0.7)
      const len = arrowLen * lenFactor

      const tailLng = lng - ux * len * 0.3
      const tailLat = lat - vy * len * 0.3
      const tipLng = lng + ux * len * 0.7
      const tipLat = lat + vy * len * 0.7

      const angle = Math.atan2(vy, ux)
      const hl = len * 0.25 * lenFactor
      const barbLLng = tipLng - Math.cos(angle - headAngle) * hl
      const barbLLat = tipLat - Math.sin(angle - headAngle) * hl
      const barbRLng = tipLng - Math.cos(angle + headAngle) * hl
      const barbRLat = tipLat - Math.sin(angle + headAngle) * hl

      features.push({
        type: 'Feature',
        geometry: {
          type: 'MultiLineString',
          coordinates: [
            [
              [tailLng, tailLat],
              [tipLng, tipLat],
            ],
            [
              [tipLng, tipLat],
              [barbLLng, barbLLat],
            ],
            [
              [tipLng, tipLat],
              [barbRLng, barbRLat],
            ],
          ],
        },
        properties: { speed: Math.round(speedMph) },
      })
    }
  }

  return { type: 'FeatureCollection', features }
}
